import { useFilesForCompanyId } from "src/api/hooks/useFiles";
import { useSigningSession } from "src/api/hooks/useSigningSession";
import { DataCard } from "./DataCard";
import { useEffect, useMemo, useState } from "react";
import { FileType, SasFileResult } from "src/types/InspectionUpdateRequest";
import { Modal } from "./Modal";
import { SigningPage } from "./SigningPage";
import useChangeTitle from "src/api/hooks/useChangeTitle";
import { lang } from "src/language/Strings";
import { useNavigate, useParams } from "react-router-dom";
import { routes } from "src/routes";
import { useForm } from "src/api/hooks/useForm";
import { ItemDescription } from "./ItemDescription";
import { Button } from "./Buttons/Button";
import useFileMutation from "src/api/hooks/useFileMutation";
import { ShowToast } from "./Toast";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "src/api/queryKeys";
import useMyUser from "src/api/hooks/useMyUser";

export function DocumentsList() {
  const signingSession = useSigningSession();
  const { companyId, documentId, signingSessionId } = useParams();
  const [clickedDocument, setClickedDocument] = useState<SasFileResult | null>(
    null
  );
  const currentDocument = useMemo(() => {
    if (!!documentId && signingSession.get.data)
      return signingSession.get.data.find((d) => d.id === Number(documentId));
    else return null;
  }, [documentId, signingSession.get.data]);
  useEffect(() => {
    const plusIconElement = document.getElementById("plus-icon-add");
    if (plusIconElement) {
      plusIconElement.addEventListener("click", () => {
        setIsCreateDocumentModalOpen(true);
      });
    }
    return () => {
      if (plusIconElement) {
        plusIconElement.removeEventListener("click", () => {});
      }
    };
  }, []);

  const nav = useNavigate();
  const { data: myUser } = useMyUser();
  useChangeTitle(lang.documents);
  const [isCreateDocumentModalOpen, setIsCreateDocumentModalOpen] =
    useState(false);

  return (
    <div className="flex flex-col h-full w-full">
      <div className="p-2 h-20 bg-White w-full min-h-20 relative">
        <div
          onClick={() => {
            setIsCreateDocumentModalOpen(true);
          }}
          className="text-PrimaryGreen text-2xl cursor-pointer absolute right-6 transition-all duration-150 ease-in-out hover:scale-110 active:scale-95 active:opacity-702 pt-4 pr-4  pl-2"
        >
          <i className="fa-solid  fa-plus mr-2  " title="Add"></i>
          <span className="group-hover:text-blue-600 dark:group-hover:text-blue-500">
            {lang.createDocument}
          </span>
        </div>
      </div>
      <div>
        {signingSession.get.data?.length ? (
          signingSession.get.data
            .filter((d) => d.fileType === FileType.Document)
            .map((d) => {
              return (
                <DataCard
                  id={`signable-document-${d.id}`}
                  key={`signable-document-${d.id}`}
                  firstRowInput={d.comment}
                  imageUrl={
                    d.signatures?.find((s) => s.userId === myUser?.id)
                      ?.signatureFile?.url
                  }
                  onClick={() => {
                    nav(
                      routes.SigningSession.DocumentById.url(companyId!, d.id)
                    );
                  }}
                  isSelected={false}
                  header={d.depiction}
                  secondRowInput={d.fileName || ""}
                  thirdRowInput={
                    d.signatures?.some(
                      (s) =>
                        s.signingStatus === "Success" && myUser?.id === s.userId
                    ) ? (
                      <>
                        <span className="text-SkaneGreen">{lang.signed}</span>
                        <i className="fa-solid fa-check text-SkaneGreen ml-2" />
                      </>
                    ) : null
                  }
                />
              );
            })
        ) : (
          <small className="text-PrimaryRed">{lang.noAddedDocuments}</small>
        )}
      </div>
      {currentDocument && (
        <ClickedDocumentModal
          setClickedDocument={() =>
            nav(routes.SigningSession.Documents.url(companyId!))
          }
          document={currentDocument}
        />
      )}
      {isCreateDocumentModalOpen && (
        <CreateDocumentModal
          setIsCreateDocumentModalOpen={setIsCreateDocumentModalOpen}
        />
      )}
    </div>
  );
}

function CreateDocumentModal({
  setIsCreateDocumentModalOpen,
}: {
  setIsCreateDocumentModalOpen: (isCreateDocumentModalOpen: boolean) => void;
}) {
  const { companyId } = useParams();
  const { Form, formData, formRef, setFormData } = useForm({
    companyId: Number(companyId),
    fileName: "",
    comment: "",
    depiction: "",
    fileType: FileType.Document,
    file: null as File | null,
  } as SasFileResult);
  const [uploadProgress, setUploadProgress] = useState(0);
  const onUploadProgress = (progress: number, fileName: string) => {
    setUploadProgress(progress);
  };
  const queryClient = useQueryClient();

  const fileMutation = useFileMutation(onUploadProgress);
  useEffect(() => {
    if (fileMutation.create.status === "success" && uploadProgress === 1) {
      setIsCreateDocumentModalOpen(false);
      setUploadProgress(0);
      queryClient.invalidateQueries({
        queryKey: queryKeys.files(companyId!, "all"),
      });
      ShowToast(lang.fileCreatedSuccessfully, "success");
    } else if (fileMutation.create.status === "error") {
      setUploadProgress(0);
      setIsCreateDocumentModalOpen(false);
      ShowToast(lang.fileCreatedError, "error");
    }
  }, [fileMutation.create.status, uploadProgress]);

  return (
    <Modal onOutsideClick={() => {}}>
      <h1 className="text-lg font-bold text-PrimaryGreen text-center">
        {lang.createDocument}
      </h1>
      <Form id="createDocumentForm" className="flex flex-col gap-2">
        <ItemDescription label={lang.documentTitle}>
          <Form.Input name="depiction" defaultValue={formData.depiction} />
        </ItemDescription>

        <ItemDescription label={lang.comment}>
          <Form.TextArea name="comment" defaultValue={formData.comment} />
        </ItemDescription>
        <ItemDescription label={lang.file}>
          <input
            type="file"
            name="document"
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                file: e.target.files?.[0] as File,
                fileName: e.target.files?.[0]?.name ?? "",
              }));
            }}
          />
        </ItemDescription>
        <div className="flex flex-row gap-2 justify-center">
          <Button
            className="bg-PrimaryGreen text-white rounded-lg p-2 border-2 text-White"
            buttonType="button"
            title={lang.close}
            onClick={() => {
              setIsCreateDocumentModalOpen(false);
            }}
          />
          <Button
            className="bg-PrimaryGreen text-white rounded-lg p-2 border-2  text-White"
            buttonType="submit"
            title={lang.createDocument}
            formId="createDocumentForm"
            onClick={() => {
              fileMutation.create.mutate(
                formData as Omit<
                  SasFileResult,
                  "files" | "url" | "id" | "signatures"
                >
              );
            }}
          />
        </div>
      </Form>
    </Modal>
  );
}

function ClickedDocumentModal({
  document,
  setClickedDocument,
}: {
  document: SasFileResult;
  setClickedDocument: React.Dispatch<
    React.SetStateAction<SasFileResult | null>
  >;
}) {
  return (
    <Modal className="w-full" onOutsideClick={() => setClickedDocument(null)}>
      <SigningPage />
    </Modal>
  );
}
